import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Headphones, Mic, MicOff, PhoneCall, PhoneOff, RefreshCcw, UserRound } from "lucide-react";
import { useOperatorConsole, CallSummary } from "@/hooks/useOperatorConsole";

/**
 * Operator dispatcher dashboard.
 *
 * Mounted at /operator. The operator connects to /ws/operator, sees a live
 * list of active kiosk sessions and can "take over" a specific call. After
 * takeover the browser bridges directly to the kiosk over the same WS — no
 * Teams, no phone, no context loss.
 *
 * URL params:
 *   ?name=Karin       operator display name shown to caller (fallback: "Operator")
 *   ?token=<secret>   optional shared secret when OPERATOR_TOKEN is configured
 */
export default function OperatorPage() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const operatorName = params.get("name") || "Operator";
    const { state, error, calls, activeCall, muted, toggleMute, connect, takeOver, hangup } = useOperatorConsole(operatorName);

    useEffect(() => { void connect(); }, [connect]);

    const onCall = state === "on_call" || state === "taking_over";

    return (
        <div className="min-h-screen w-full bg-gray-950 text-gray-100">
            <header className="border-b border-gray-800 bg-gray-900 px-6 py-4">
                <div className="mx-auto flex max-w-6xl items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-parking-300">parking.audiona.ai — dispatcher</h1>
                        <p className="text-sm text-gray-400">Ingelogd als <span className="font-medium text-gray-200">{operatorName}</span></p>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <StatusPill state={state} />
                    </div>
                </div>
            </header>

            {error && (
                <div className="mx-auto mt-4 flex max-w-6xl items-center gap-2 rounded-lg bg-red-900/40 px-4 py-2 text-red-300">
                    <AlertCircle className="h-4 w-4" />
                    <span>{error}</span>
                </div>
            )}

            <main className="mx-auto max-w-6xl px-6 py-8">
                {onCall && activeCall ? (
                    <ActiveCallPanel
                        call={activeCall}
                        operatorName={operatorName}
                        muted={muted}
                        onToggleMute={toggleMute}
                        onHangup={hangup}
                    />
                ) : (
                    <CallList calls={calls} onTake={takeOver} state={state} />
                )}
            </main>
        </div>
    );
}

function StatusPill({ state }: { state: string }) {
    const label: Record<string, string> = {
        idle: "Stand-by",
        connecting: "Verbinden…",
        listening_dashboard: "Live",
        taking_over: "Overnemen…",
        on_call: "In gesprek",
        ended: "Verbroken",
        error: "Fout",
    };
    const color: Record<string, string> = {
        idle: "bg-gray-600",
        connecting: "bg-yellow-500",
        listening_dashboard: "bg-parking-400",
        taking_over: "bg-yellow-500",
        on_call: "bg-red-500",
        ended: "bg-gray-500",
        error: "bg-red-600",
    };
    return (
        <span className="inline-flex items-center gap-2 rounded-full bg-gray-800 px-3 py-1">
            <span className={`h-2 w-2 rounded-full ${color[state] || "bg-gray-500"}`} />
            {label[state] || state}
        </span>
    );
}

function CallList({ calls, onTake, state }: { calls: CallSummary[]; onTake: (id: string) => Promise<void>; state: string }) {
    if (state === "connecting") {
        return <div className="text-gray-400">Verbinden met dispatcher…</div>;
    }
    if (calls.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-gray-800 bg-gray-900/60 p-10 text-center text-gray-500">
                <RefreshCcw className="mx-auto mb-2 h-6 w-6 opacity-50" />
                Geen actieve gesprekken. Nieuwe binnenkomende gesprekken verschijnen hier automatisch.
            </div>
        );
    }
    const sorted = [...calls].sort((a, b) => {
        const rank = (c: CallSummary) =>
            c.urgency === "emergency" ? 0 :
            c.state === "escalation_requested" ? 1 :
            c.urgency === "high" ? 2 : 5;
        return rank(a) - rank(b);
    });
    return (
        <div className="grid gap-4 md:grid-cols-2">
            {sorted.map((c) => <CallCard key={c.session_id} call={c} onTake={onTake} />)}
        </div>
    );
}

function CallCard({ call, onTake }: { call: CallSummary; onTake: (id: string) => Promise<void> }) {
    const urgent = call.state === "escalation_requested" || call.urgency === "emergency" || call.urgency === "high";
    const mm = String(Math.floor(call.elapsed_s / 60)).padStart(2, "0");
    const ss = String(call.elapsed_s % 60).padStart(2, "0");
    return (
        <div className={`rounded-xl border p-5 shadow-lg ${urgent ? "border-red-500/60 bg-red-950/30" : "border-gray-800 bg-gray-900/60"}`}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <UserRound className="h-4 w-4" />
                        <span>{call.location || call.site}{call.machine ? ` · paal ${call.machine}` : ""}</span>
                    </div>
                    <div className="mt-1 font-mono text-xs text-gray-500">{call.session_id}</div>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-semibold tabular-nums">{mm}:{ss}</div>
                    <UrgencyBadge state={call.state} urgency={call.urgency} />
                </div>
            </div>

            <div className="mt-4 space-y-1 text-sm">
                {call.caller_plate && (
                    <div>
                        <span className="text-gray-500">Kenteken:</span>{" "}
                        <span className="font-mono font-semibold text-gray-100">{call.caller_plate}</span>
                    </div>
                )}
                {call.last_caller_text && (
                    <div>
                        <span className="text-gray-500">Beller:</span>{" "}
                        <span className="text-gray-200">"{call.last_caller_text}"</span>
                    </div>
                )}
                {call.last_bot_text && (
                    <div>
                        <span className="text-gray-500">Fenna:</span>{" "}
                        <span className="text-gray-300">"{call.last_bot_text}"</span>
                    </div>
                )}
            </div>

            <div className="mt-5 flex gap-2">
                <button
                    onClick={() => void onTake(call.session_id)}
                    disabled={call.has_operator || call.state === "ended"}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                        urgent
                            ? "bg-red-600 hover:bg-red-500 text-white"
                            : "bg-parking-600 hover:bg-parking-500 text-white"
                    } disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-400`}
                >
                    <PhoneCall className="h-4 w-4" />
                    {call.has_operator ? "Bezet" : "Overnemen"}
                </button>
            </div>
        </div>
    );
}

function UrgencyBadge({ state, urgency }: { state: CallSummary["state"]; urgency: CallSummary["urgency"] }) {
    if (state === "escalation_requested") {
        return <span className="mt-1 inline-block rounded-full bg-red-600/80 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">Vraagt hulp</span>;
    }
    if (urgency === "emergency") {
        return <span className="mt-1 inline-block rounded-full bg-red-700 px-2 py-0.5 text-xs font-semibold uppercase text-white">Noodgeval</span>;
    }
    if (urgency === "high") {
        return <span className="mt-1 inline-block rounded-full bg-orange-600 px-2 py-0.5 text-xs font-semibold uppercase text-white">Hoog</span>;
    }
    return <span className="mt-1 inline-block rounded-full bg-gray-700 px-2 py-0.5 text-xs uppercase text-gray-300">{state === "with_fenna" ? "Fenna" : state}</span>;
}

function ActiveCallPanel({
    call,
    operatorName,
    muted,
    onToggleMute,
    onHangup,
}: {
    call: CallSummary;
    operatorName: string;
    muted: boolean;
    onToggleMute: () => void;
    onHangup: () => Promise<void>;
}) {
    return (
        <div className="rounded-2xl border border-parking-400/40 bg-gray-900/80 p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Headphones className="h-6 w-6 text-parking-300" />
                    <div>
                        <div className="text-xs uppercase tracking-widest text-gray-400">In gesprek als {operatorName}</div>
                        <div className="text-xl font-semibold">{call.location || call.site} <span className="text-gray-500">· paal {call.machine || "?"}</span></div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onToggleMute}
                        className={`flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition ${
                            muted
                                ? "bg-yellow-500 text-gray-900 hover:bg-yellow-400"
                                : "bg-gray-700 text-gray-100 hover:bg-gray-600"
                        }`}
                        title="Mute je microfoon (m)"
                    >
                        {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                        {muted ? "Gemuted" : "Mute"}
                    </button>
                    <button
                        onClick={() => void onHangup()}
                        className="flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 font-semibold text-white transition hover:bg-red-500"
                    >
                        <PhoneOff className="h-5 w-5" /> Beëindigen
                    </button>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <InfoTile label="Kenteken beller" value={call.caller_plate || "onbekend"} mono />
                <InfoTile label="Site" value={call.site} mono />
            </div>

            <div className="mt-4 rounded-xl bg-gray-950/60 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Live transcript</div>
                <div className="mt-2 max-h-48 space-y-2 overflow-y-auto text-sm">
                    {call.last_caller_text ? (
                        <div>
                            <span className="mr-2 inline-block rounded bg-parking-700/60 px-1.5 py-0.5 text-xs uppercase text-parking-100">Beller</span>
                            <span className="text-gray-100">"{call.last_caller_text}"</span>
                        </div>
                    ) : null}
                    {call.last_bot_text ? (
                        <div>
                            <span className="mr-2 inline-block rounded bg-gray-700 px-1.5 py-0.5 text-xs uppercase text-gray-200">Fenna</span>
                            <span className="text-gray-300">"{call.last_bot_text}"</span>
                        </div>
                    ) : null}
                    {!call.last_caller_text && !call.last_bot_text && (
                        <div className="text-gray-500">Nog geen tekst — spraak-naar-tekst komt live binnen.</div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex items-center gap-3 text-sm text-gray-400">
                <span className={`inline-block h-2 w-2 animate-pulse rounded-full ${muted ? "bg-yellow-500" : "bg-red-500"}`} />
                {muted ? "Microfoon staat uit — beller hoort je niet" : "Audio actief · uw microfoon staat aan"}
            </div>
        </div>
    );
}

function InfoTile({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
    return (
        <div className="rounded-xl bg-gray-950/60 p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`mt-1 text-lg text-gray-100 ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}
